import React, { useEffect, useRef } from 'react';
import { Sortable } from '@shopify/draggable';
import { Article } from '../types/article';
import { reorderArticles } from '../api/articles';
import ArticleItem from './ArticleItem';

interface ArticleListProps {
  articles: Article[];
  categories: string[];
  selectedCategory: string;
  onArticlesChange: (articles: Article[]) => void;
  onArticleUpdate: (article: Article) => void;
  onArticleDelete: (id: number) => void;
  selectedDate: string;
  isDateConfirmed: boolean;
  onConfirmDate: () => void;
  onUnconfirmDate: () => void;
}

const ArticleList: React.FC<ArticleListProps> = ({
  articles,
  categories,
  selectedCategory,
  onArticlesChange,
  onArticleUpdate,
  onArticleDelete,
  selectedDate,
  isDateConfirmed,
  onConfirmDate,
  onUnconfirmDate,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sortableRef = useRef<Sortable | null>(null);

  useEffect(() => {
    if (!containerRef.current || !selectedCategory) {
      // Destroy existing sortable if category is deselected
      if (sortableRef.current) {
        sortableRef.current.destroy();
        sortableRef.current = null;
      }
      return;
    }

    // Create sortable only when a category is selected
    const sortable = new Sortable(containerRef.current, {
      draggable: '.article-item',
      handle: '.drag-handle',
      mirror: {
        constrainDimensions: true,
        xAxis: false,
      },
    });

    sortable.on('sortable:stop', () => {
      setTimeout(async () => {
        if (!containerRef.current) return;

        const items = containerRef.current.querySelectorAll('.article-item');
        const ids = Array.from(items).map((item) => parseInt((item as HTMLElement).dataset.id || '0'));

        try {
          const result = await reorderArticles(ids);
          if (result.sequences) {
            const updatedArticles = articles.map((article) => {
              const newSeq = result.sequences[article.id];
              if (newSeq !== undefined) {
                return { ...article, sequence: newSeq };
              }
              return article;
            });
            updatedArticles.sort((a, b) => a.sequence - b.sequence);
            onArticlesChange(updatedArticles);
          }
        } catch (error) {
          console.error('Reorder error:', error);
          alert('순서 변경에 실패했습니다.');
        }
      }, 50);
    });

    sortableRef.current = sortable;

    return () => {
      sortable.destroy();
    };
  }, [selectedCategory, articles, onArticlesChange]);

  if (articles.length === 0) {
    return (
      <div className="empty-state">
        <p>해당하는 기사가 없습니다.</p>
      </div>
    );
  }

  const showDragHandle = !!selectedCategory;
  const showCategorySelect = !selectedCategory;

  return (
    <div className="article-list-wrapper">
      <div ref={containerRef} className="article-list">
        {articles.map((article) => (
          <ArticleItem
            key={article.id}
            article={article}
            categories={categories}
            showDragHandle={showDragHandle}
            showCategorySelect={showCategorySelect}
            onArticleUpdate={onArticleUpdate}
            onArticleDelete={onArticleDelete}
          />
        ))}
      </div>
      <div className="confirm-date-section">
        {isDateConfirmed ? (
          <button className="btn-confirm btn-confirmed" onClick={onUnconfirmDate}>
            {selectedDate} 확정 취소
          </button>
        ) : (
          <button className="btn-confirm" onClick={onConfirmDate}>
            {selectedDate} 확정
          </button>
        )}
      </div>
    </div>
  );
};

export default ArticleList;
